import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { milliseconds } from "../../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { ManualOverrideInfo } from "../../commonSubSchemas/manualOverrideInfo.js";
import { RecentSensorDataPoint } from "../../commonSubSchemas/recentSensorDataPoint.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type { Gen4NetworkInfoFieldsWithDefaults, Gen4NetworkInfoSchemaTypes } from "../../types/gen4NetworkInfo.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";
import { SignalMetrics } from "./signalMetrics.js";

const SCHEMA_KEY = "Gen4NetworkInfo" as const;

// TODO: convert to responseOnly schema class
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<Gen4NetworkInfo>>(...addSchemaKeyHook(SCHEMA_KEY))
export class Gen4NetworkInfo extends SchemaBase<Gen4NetworkInfo, Gen4NetworkInfoFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /**
     * The date that the last network test was started on this device. This is used to determine if the device
     * is currently undergoing a network test. See Gen4Config.ts for details on how long a network test
     * lasts
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastNetworkTestStartDate?: Date;

    /**
     * When the sensor fPort last delivered an uplink. Read when deciding how far back to dedupe
     * incoming sensor data points.
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastSensorUplinkDate?: Date;

    /**
     * RSSI and SNR of the connection to each gateway as measured during the most recent uplink
     */
    @validate({
        childClass: SignalMetrics,
        childClassTransform: (object) => z.array(object).optional(),
        responseOnly: true,
    })
    @prop({ type: () => SignalMetrics, required: true, default: [] }, PropType.ARRAY)
    public lastSignalMetrics: SignalMetrics[] = [];

    /**
     * Overall packet loss, up and down. Between 0 and 1, lower is better
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public packetLoss: number = 0;

    /**
     * Percentage of uplinks that were sent by the device, but not received by the backend. Between 0 and 1, lower is better
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public uplinkPacketLoss: number = 0;

    /**
     * Percentage of downlinks that were sent by the backend, but not received by the device. Between 0 and 1, lower is better
     */
    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public downlinkPacketLoss: number = 0;

    @validate({
        childClass: ManualOverrideInfo,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => ManualOverrideInfo, required: false })
    public manualOverrideInfo?: ManualOverrideInfo;

    /**
     * Device uplink period in milliseconds.
     *
     * Stored here as a multiple of 15 seconds, but encoded
     * during downlinks as ⌊currentStandardUplinkPeriod / 15000⌋.
     *
     * Gen4 devices support a value range of [1, 254] which represents
     * the number of 15-second intervals between uplinks, yielding an
     * effective range of [15 seconds, 63.5 minutes] between uplinks.
     */
    @validate({ zodValidator: milliseconds.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public currentStandardUplinkPeriod?: number;

    /**
     * The most recent gen4ValvePulseRecord that was measured by this device, includes the solenoid pulse
     * code.
     */
    @validate({
        childClass: RecentSensorDataPoint,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => RecentSensorDataPoint, required: false })
    public lastValvePulseRecord?: RecentSensorDataPoint<"gen4ValvePulseRecord">;

    /**
     * The most recent battery voltage reading that was measured by this device.
     */
    @validate({
        childClass: RecentSensorDataPoint,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => RecentSensorDataPoint, required: false })
    public lastBatteryVoltageReading?: RecentSensorDataPoint<"batteryVoltage">;

    static getters = {} as const satisfies Record<string, (self: Gen4NetworkInfo) => any>;

    static utilities = {
        getDefaultGen4NetworkInfo: (): Gen4NetworkInfo =>
            ({
                downlinkPacketLoss: 0,
                manualOverrideInfo: {
                    remoteOverrideSetting: "none",
                    remoteOverrideSettingLastUpdated: new Date(0),
                    deviceOverrideSetting: "none",
                    deviceOverrideSettingLastUpdated: new Date(0),
                },
                lastSignalMetrics: [],
                packetLoss: 0,
                uplinkPacketLoss: 0,
            }) as Gen4NetworkInfo,
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<Gen4NetworkInfoSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<Gen4NetworkInfo, Gen4NetworkInfoFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

Gen4NetworkInfo.dtoValidators = getZodSchemaFromClass(
    Gen4NetworkInfo,
) as unknown as DtoValidatorTypes<Gen4NetworkInfoSchemaTypes>;
